import { useEffect, useMemo, useState } from 'react';

import type { PrescriptionT } from '@lotushealth/lotushealth_js_sdk/schemas';
import { ArrowUpDown, Eye } from 'lucide-react';

import {
  buildRenewalBadge,
  medicationStatusLabel,
  usePatientPrescriptionsQuery,
} from '@/entities/rx';
import { ANALYTICS_EVENTS, analytics, logger } from '@/shared';
import {
  compareTableValues,
  getRowKeyFromOptionalId,
  toTableCellText,
  type SortDirection,
} from '@/shared/lib/table';
import {
  DENSE_TABLE_CELL_CLASS,
  DENSE_TABLE_CONTAINER_CLASS,
  DENSE_TABLE_HEAD_CLASS,
  DENSE_TABLE_HEADER_CLASS,
  DENSE_TABLE_HEADER_ROW_CLASS,
  DENSE_TABLE_ROW_CLASS,
  DENSE_TABLE_SORT_BUTTON_CLASS,
} from '@/shared/ui/primitives';
import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/shadcn/alert';
import { Badge } from '@/shared/ui/shadcn/badge';
import { Button } from '@/shared/ui/shadcn/button';
import { statusToneClassName } from '@/shared/ui/statusTone';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/shadcn/table';

import { PRESCRIPTIONS_TABLE_CONFIG } from '../model';
import { CancelPrescriptionAction } from './CancelPrescriptionAction';
import { PrescriptionLogsSheet } from './PrescriptionLogsSheet';

interface RxPrescriptionsTableProps {
  userId: string;
  openPrescriptionId?: string;
  onConsumeOpenPrescription?: () => void;
}

export function RxPrescriptionsTable({
  userId,
  openPrescriptionId,
  onConsumeOpenPrescription,
}: RxPrescriptionsTableProps) {
  const prescriptionsQuery = usePatientPrescriptionsQuery(userId, { synchronize: true });
  const prescriptions = useMemo(
    () => prescriptionsQuery.data?.prescriptions ?? [],
    [prescriptionsQuery.data]
  );

  const [sortColumn, setSortColumn] = useState<keyof PrescriptionT>('WrittenDate');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [logsOpen, setLogsOpen] = useState(false);
  const [selectedPrescriptionId, setSelectedPrescriptionId] = useState<string>('');
  const [selectedPrescriptionName, setSelectedPrescriptionName] = useState<string>('');
  const [selectedPharmacyId, setSelectedPharmacyId] = useState<string | undefined>(undefined);

  const sortedPrescriptions = useMemo(() => {
    const next = [...prescriptions];
    next.sort((a, b) => {
      const result = compareTableValues(a[sortColumn], b[sortColumn]);
      return sortDirection === 'asc' ? result : -result;
    });
    return next;
  }, [prescriptions, sortColumn, sortDirection]);

  const missingIdCount = useMemo(
    () =>
      prescriptions.filter((p) => p.PrescriptionId === null || p.PrescriptionId === undefined)
        .length,
    [prescriptions]
  );

  useEffect(() => {
    if (missingIdCount <= 0) return;
    logger.warn('DoseSpot prescriptions missing PrescriptionId', { userId, missingIdCount });
  }, [missingIdCount, userId]);

  useEffect(() => {
    if (!prescriptionsQuery.isSuccess) return;
    void analytics.track({
      name: ANALYTICS_EVENTS.rx.prescriptionsViewed,
      context: {
        actor: 'clinician',
        patientUserId: userId,
      },
      properties: { count: prescriptions.length },
    });
  }, [prescriptions.length, prescriptionsQuery.isSuccess, userId]);

  const externalSelectedPrescription = useMemo(() => {
    if (openPrescriptionId === undefined || openPrescriptionId === '') {
      return null;
    }

    const matchedPrescription = prescriptions.find((prescription) => {
      return String(prescription.PrescriptionId ?? '') === openPrescriptionId;
    });
    if (
      matchedPrescription === undefined ||
      matchedPrescription.PrescriptionId === null ||
      matchedPrescription.PrescriptionId === undefined
    ) {
      return null;
    }
    return matchedPrescription;
  }, [openPrescriptionId, prescriptions]);

  const effectivePrescriptionId =
    selectedPrescriptionId !== ''
      ? selectedPrescriptionId
      : externalSelectedPrescription?.PrescriptionId != null
        ? String(externalSelectedPrescription.PrescriptionId)
        : '';
  const effectivePrescriptionName =
    selectedPrescriptionName !== ''
      ? selectedPrescriptionName
      : externalSelectedPrescription !== null
        ? toTableCellText(externalSelectedPrescription.DisplayName)
        : '';
  const effectivePharmacyId =
    selectedPharmacyId !== undefined
      ? selectedPharmacyId
      : externalSelectedPrescription?.PharmacyId != null
        ? String(externalSelectedPrescription.PharmacyId)
        : undefined;
  const isLogsOpen = logsOpen || externalSelectedPrescription !== null;

  if (prescriptionsQuery.isLoading) {
    return <div className="text-sm text-muted-foreground">Loading prescriptions…</div>;
  }

  if (prescriptionsQuery.isError) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Could not load DoseSpot prescriptions</AlertTitle>
        <AlertDescription>
          {prescriptionsQuery.error instanceof Error
            ? prescriptionsQuery.error.message
            : 'An unknown error occurred.'}
        </AlertDescription>
      </Alert>
    );
  }

  if (prescriptions.length === 0) {
    return <div className="text-sm text-muted-foreground">No DoseSpot prescriptions found.</div>;
  }

  return (
    <>
      <PrescriptionLogsSheet
        userId={userId}
        prescriptionId={effectivePrescriptionId}
        prescriptionName={effectivePrescriptionName}
        pharmacyId={effectivePharmacyId}
        open={isLogsOpen}
        onOpenChange={(open) => {
          setLogsOpen(open);
          if (!open) {
            setSelectedPrescriptionId('');
            setSelectedPrescriptionName('');
            setSelectedPharmacyId(undefined);
            onConsumeOpenPrescription?.();
          }
        }}
      />

      <div className={DENSE_TABLE_CONTAINER_CLASS}>
        <Table>
          <TableHeader className={DENSE_TABLE_HEADER_CLASS}>
            <TableRow className={DENSE_TABLE_HEADER_ROW_CLASS}>
              {PRESCRIPTIONS_TABLE_CONFIG.columnOrder.map((field) => {
                const headerLabel =
                  PRESCRIPTIONS_TABLE_CONFIG.headerByColumn?.[field] ?? String(field);
                const sortableColumns = PRESCRIPTIONS_TABLE_CONFIG.sortableColumns ?? [];
                const isSortable = sortableColumns.includes(field);

                return (
                  <TableHead key={field} className={DENSE_TABLE_HEAD_CLASS}>
                    {isSortable ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className={DENSE_TABLE_SORT_BUTTON_CLASS}
                        onClick={() => {
                          if (sortColumn === field) {
                            setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
                            return;
                          }
                          setSortColumn(field);
                          setSortDirection('asc');
                        }}
                      >
                        {headerLabel}
                        <ArrowUpDown className="ml-1.5 size-3" aria-hidden="true" />
                      </Button>
                    ) : (
                      headerLabel
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          </TableHeader>

          <TableBody>
            {sortedPrescriptions.map((prescription, index) => {
              const canViewLogs =
                prescription.PrescriptionId !== null && prescription.PrescriptionId !== undefined;
              const nameLabel = toTableCellText(prescription.DisplayName);
              const renewal = buildRenewalBadge(prescription);
              const openLogs = () => {
                if (!canViewLogs) return;
                void analytics.track({
                  name: ANALYTICS_EVENTS.rx.prescriptionLogsOpened,
                  context: {
                    actor: 'clinician',
                    patientUserId: userId,
                  },
                  properties: { prescriptionIdPresent: canViewLogs },
                });
                setSelectedPrescriptionId(String(prescription.PrescriptionId));
                setSelectedPrescriptionName(nameLabel);
                setSelectedPharmacyId(
                  prescription.PharmacyId != null ? String(prescription.PharmacyId) : undefined
                );
                setLogsOpen(true);
              };

              return (
                <TableRow
                  key={getRowKeyFromOptionalId(prescription.PrescriptionId, index)}
                  className={DENSE_TABLE_ROW_CLASS}
                >
                  {PRESCRIPTIONS_TABLE_CONFIG.columnOrder.map((field) => {
                    const value = prescription[field];
                    const badgeColumns = PRESCRIPTIONS_TABLE_CONFIG.badgeColumns ?? [];

                    if (field === 'DisplayName') {
                      return (
                        <TableCell key={field} className={DENSE_TABLE_CELL_CLASS}>
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex min-w-0 items-center gap-2">
                              <span className="truncate">{nameLabel}</span>
                              {renewal.show ? (
                                <Badge
                                  className={`${statusToneClassName('info')} rounded-full`}
                                  title={renewal.note ?? undefined}
                                  aria-label={
                                    renewal.note !== null
                                      ? `Renewal: ${renewal.note}`
                                      : 'Renewal request'
                                  }
                                  data-testid="renewal-badge"
                                >
                                  {renewal.label}
                                </Badge>
                              ) : null}
                            </div>
                            {canViewLogs ? (
                              <div className="flex shrink-0 items-center gap-2">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="chip"
                                  className="border-secondary dark:border-secondary"
                                  aria-label={`View details for ${nameLabel}`}
                                  onClick={() => {
                                    openLogs();
                                  }}
                                >
                                  <Eye className="size-3.5" aria-hidden="true" />
                                  View details
                                </Button>
                                <CancelPrescriptionAction
                                  userId={userId}
                                  dosespotPrescriptionId={String(prescription.PrescriptionId)}
                                  prescriptionName={nameLabel}
                                />
                              </div>
                            ) : null}
                          </div>
                        </TableCell>
                      );
                    }

                    if (field === 'MedicationStatus') {
                      const statusLabel = medicationStatusLabel(prescription.MedicationStatus);
                      return (
                        <TableCell key={field} className={DENSE_TABLE_CELL_CLASS}>
                          <Badge
                            className={`${statusToneClassName(statusLabel.tone)} rounded-full`}
                            data-testid="medication-status-badge"
                          >
                            {statusLabel.label}
                          </Badge>
                        </TableCell>
                      );
                    }

                    return (
                      <TableCell key={field} className={DENSE_TABLE_CELL_CLASS}>
                        {badgeColumns.includes(field) ? (
                          <Badge variant="secondary" className="rounded-full">
                            {toTableCellText(value)}
                          </Badge>
                        ) : (
                          toTableCellText(value)
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </>
  );
}
